
import React from 'react';
import { 
  Card, 
  CardContent
} from '@/components/ui/card';
import { ProjectFile } from '@/types/collaboration';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { 
  Download, 
  FileAudio, 
  FileVideo, 
  FileImage, 
  FileText,
  MessageSquare,
  MoreHorizontal,
  Play
} from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Badge } from '@/components/ui/badge';

interface FileCardProps {
  file: ProjectFile;
  onPlay?: (file: ProjectFile) => void;
  onDownload?: (file: ProjectFile) => void;
  onViewComments?: (file: ProjectFile) => void;
}

const FileCard: React.FC<FileCardProps> = ({ 
  file, 
  onPlay, 
  onDownload, 
  onViewComments 
}) => {
  const getFileIcon = () => {
    switch (file.type) {
      case 'audio':
        return <FileAudio className="h-5 w-5 text-blue-500" />;
      case 'video':
        return <FileVideo className="h-5 w-5 text-red-500" />;
      case 'image':
        return <FileImage className="h-5 w-5 text-green-500" />;
      default:
        return <FileText className="h-5 w-5 text-amber-500" />;
    }
  };
  
  const formatFileSize = (size: string) => {
    return size;
  };
  
  const handlePlay = () => {
    if (onPlay) {
      onPlay(file);
    }
  };
  
  const handleDownload = () => {
    if (onDownload) {
      onDownload(file);
    }
  };
  
  const handleViewComments = () => {
    if (onViewComments) {
      onViewComments(file);
    }
  };
  
  const isPlayable = file.type === 'audio' || file.type === 'video';
  
  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow animate-fade-in">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex gap-3">
            {getFileIcon()}
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm line-clamp-1">{file.name}</p>
                {file.version > 1 && (
                  <Badge variant="outline" className="text-xs">v{file.version}</Badge>
                )}
              </div>
              <div className="flex items-center text-xs text-muted-foreground mt-1">
                <span>{file.format} • {formatFileSize(file.size)}</span>
                <span className="mx-1">•</span>
                <span>Uploaded {formatDistanceToNow(new Date(file.uploadedAt), { addSuffix: true })}</span>
              </div>
              
              <div className="flex gap-2 mt-2">
                {isPlayable && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 px-2 text-xs"
                    onClick={handlePlay}
                  >
                    <Play className="h-3 w-3 mr-1" />
                    Play
                  </Button>
                )}
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-7 px-2 text-xs"
                  onClick={handleDownload}
                >
                  <Download className="h-3 w-3 mr-1" />
                  Download
                </Button>
                
                {!!file.comments?.length && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 px-2 text-xs"
                    onClick={handleViewComments}
                  >
                    <MessageSquare className="h-3 w-3 mr-1" />
                    {file.comments.length} Comment{file.comments.length !== 1 ? 's' : ''}
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Download</DropdownMenuItem>
              <DropdownMenuItem>Share</DropdownMenuItem>
              <DropdownMenuItem>Rename</DropdownMenuItem>
              <DropdownMenuItem>View History</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive">Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};

export default FileCard;
